import React, { useState, useEffect, useMemo } from 'react';
import styles from './LinearProgressBar.module.css';

/**
 * LinearProgressBar component that displays progress over a specified duration (via CSS animation or random jumps),
 * with a fixed percentage or value/maxValue ratio, or as an indeterminate animated progress state.
 *
 * @param {Object} props
 * @param {number} [props.durationInSeconds] - Duration in seconds to animate 0% to 100%
 * @param {number} [props.value] - Controlled progress value
 * @param {number} [props.maxValue=100] - Maximum value for progress calculation (used with value)
 * @param {boolean} [props.randomizeTimerJumps=false] - If true, progress jumps in random segments over durationInSeconds instead of moving linearly
 * @param {'determinate' | 'indeterminate'} [props.variant] - Mode of progress display
 * @param {string} [props.className] - Container wrapper CSS class
 * @param {string} [props.barClassName] - Inner bar CSS class
 * @param {number|string} [props.height=4] - Height of progress bar in px or CSS string
 * @param {string} [props.color] - Custom fill, overriding the default gradient. Any CSS
 *   background value - a solid color or a gradient.
 */
const LinearProgressBar = ({
  durationInSeconds,
  value,
  maxValue = 100,
  randomizeTimerJumps = false,
  variant,
  className = '',
  barClassName = '',
  height = 4,
  color,
}) => {
  const mode =
    variant || (durationInSeconds !== undefined || value !== undefined ? 'determinate' : 'indeterminate');

  const containerStyle = {
    height: typeof height === 'number' ? `${height}px` : height,
  };

  const isTimerMode = mode === 'determinate' && durationInSeconds !== undefined && value === undefined;

  const clampedValue = useMemo(() => {
    if (value === undefined) return undefined;
    const maxVal = typeof maxValue === 'number' && maxValue > 0 ? maxValue : 100;
    return Math.min(Math.max(value, 0), maxVal);
  }, [value, maxValue]);

  const calculatedPercentage = useMemo(() => {
    if (clampedValue === undefined) return undefined;
    const maxVal = typeof maxValue === 'number' && maxValue > 0 ? maxValue : 100;
    return (clampedValue / maxVal) * 100;
  }, [clampedValue, maxValue]);

  const [jumpProgress, setJumpProgress] = useState(0);

  useEffect(() => {
    if (!isTimerMode || !randomizeTimerJumps || !durationInSeconds) {
      setJumpProgress(0);
      return;
    }

    setJumpProgress(0);
    const totalMs = durationInSeconds * 1000;
    const timeouts = [];

    // First jump between 3% and 7%
    const firstJumpPct = Math.floor(Math.random() * 5) + 3; // 3 to 7
    const firstDelay = Math.floor(Math.random() * 300) + 200; // 200ms to 500ms
    let currentPct = firstJumpPct;
    let elapsed = firstDelay;

    const firstTimeoutId = setTimeout(() => {
      setJumpProgress(firstJumpPct);
    }, firstDelay);
    timeouts.push(firstTimeoutId);

    while (elapsed < totalMs - 800 && currentPct < 99) {
      // Step duration between 0.5s and 2.0s (500ms to 2000ms) to allow bigger jumps
      const stepDuration = Math.floor(Math.random() * 1500) + 500;

      elapsed += stepDuration;
      if (elapsed >= totalMs - 300) break;

      const remainingTime = totalMs - elapsed;
      const remainingPct = 100 - currentPct;
      const estimatedRemainingSteps = Math.max(1, Math.round(remainingTime / 1250));
      const avgInc = remainingPct / estimatedRemainingSteps;

      // Allow bigger random jumps around average increment
      const maxInc = Math.max(2, Math.floor(avgInc * 1.8));
      const minInc = Math.max(1, Math.floor(avgInc * 0.4));
      const jumpInc = Math.min(remainingPct, Math.floor(Math.random() * (maxInc - minInc + 1)) + minInc);

      currentPct += jumpInc;

      const targetPct = currentPct;
      const timeoutId = setTimeout(() => {
        setJumpProgress(targetPct);
      }, elapsed);
      timeouts.push(timeoutId);
    }

    const finalTimeoutId = setTimeout(() => {
      setJumpProgress(100);
    }, totalMs);
    timeouts.push(finalTimeoutId);

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [isTimerMode, randomizeTimerJumps, durationInSeconds]);

  const getFillStyle = () => {
    const style = {};
    if (color) {
      // `background` rather than `backgroundColor`, so a caller-supplied color still wins
      // over the default gradient fill - and so a gradient can be passed in too.
      style.background = color;
    }

    if (isTimerMode) {
      if (randomizeTimerJumps) {
        style.width = `${jumpProgress}%`;
      } else {
        style.animationDuration = `${durationInSeconds}s`;
      }
    } else if (mode === 'determinate' && calculatedPercentage !== undefined) {
      style.width = `${calculatedPercentage}%`;
    }

    return style;
  };

  const getFillClass = () => {
    if (mode === 'indeterminate') return styles.indeterminate;
    if (isTimerMode) {
      return randomizeTimerJumps ? styles.determinate : styles.timerProgress;
    }
    return styles.determinate;
  };

  const ariaValueNow =
    mode === 'indeterminate'
      ? undefined
      : isTimerMode
      ? randomizeTimerJumps
        ? Math.round(jumpProgress)
        : undefined
      : clampedValue !== undefined
      ? Math.round(clampedValue)
      : undefined;

  const ariaValueMax = value !== undefined && maxValue !== undefined ? maxValue : 100;

  return (
    <div
      className={[styles.track, className].filter(Boolean).join(' ')}
      style={containerStyle}
      role="progressbar"
      aria-valuenow={ariaValueNow}
      aria-valuemin={0}
      aria-valuemax={ariaValueMax}
    >
      <div
        className={[styles.fill, getFillClass(), barClassName].filter(Boolean).join(' ')}
        style={getFillStyle()}
      />
    </div>
  );
};

export default LinearProgressBar;

